'use client';

import { useEffect, useRef } from 'react';
import { SPHERE_VERT, SPHERE_FRAG } from './sphereShaders';
import styles from './SphereOrb.module.css';

/* Faithful React port of Desktop/sphere-orb.html (1:1 with the app's
   LiquidSphereShader.metal). Exact shaders + uniforms. The marble's ramp
   is fixed (navy → recovery blue → sleep teal → movement terracotta); the
   STATE is driven by heartRate (flow speed), redness (low-score warning
   continents), grayscale (under-recovered / no-data), loading. State eases
   slowly so the material morphs colour like a slider, never snaps.
   Transparent (premultiplied) — no box, no glow. */

const R = {
  sphereRadius: 0.9,
  lightDir: [-0.7, 0.7] as [number, number],
  saturation: 1.0,
  contrast: 1.0,
  specular: 0.2,
  rimIntensity: 0.58,
  ambient: 0.86,
  motionSpeed: 0.42,
  restingHR: 60,
  dprCap: 1.5,
};
const RAMP = {
  baseDeep: [0.05, 0.09, 0.18],
  rec: [0.231, 0.51, 0.965],
  slp: [0.0, 0.757, 0.773],
  mv: [0.851, 0.392, 0.204],
  red: [0.898, 0.282, 0.302],
};

type RGB = [number, number, number];

export interface OrbState {
  heartRate: number; // bpm → flow speed
  redness: number; // 0–1 warning-red continents bleed in
  green: number; // 0–1 peak-recovery green continents bleed in
  grayscale: number; // 0–1 desaturate
  loading: number; // 0–1 computing
  tint: RGB; // per-state hue the marble leans into
  tintAmt: number; // 0–1 strength of the hue lean
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const SphereOrb = ({ state, spinRef, zoomRef, radius, fixedDiameterPx, readable = false, className, 'aria-label': ariaLabel = 'Signal orb' }: {
  state: OrbState;
  /** extra yaw (radians) added on top of the idle drift — lets the
      entrance REVOLVE the marble about its own vertical axis */
  spinRef?: React.MutableRefObject<number>;
  /** drives u_zoom each frame: 0 = sphere, 1 = flat full-screen marble field.
      Lets a caller morph the orb between a full-bleed field and the sphere. */
  zoomRef?: React.MutableRefObject<number>;
  /** override the sphere radius (default 0.9), as a fraction of the canvas
      half-height. Smaller = a smaller ball inside the full-bleed canvas. */
  radius?: number;
  /** pin the sphere to a FIXED on-screen diameter (px), independent of the
      canvas/viewport size — recomputed on resize. Overrides `radius`. */
  fixedDiameterPx?: number;
  /** keep the drawing buffer so another WebGL scene can read this canvas as a
      texture (the glass marble morphs into the orb's exact pixels) */
  readable?: boolean;
  className?: string;
  'aria-label'?: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const target = useRef<OrbState>(state);
  useEffect(() => { target.current = state; }, [state]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const opts = { alpha: true, premultipliedAlpha: true, antialias: false, preserveDrawingBuffer: readable };
    const gl = (canvas.getContext('webgl', opts) || canvas.getContext('experimental-webgl', opts)) as WebGLRenderingContext | null;
    if (!gl) return;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const compile = (t: number, s: string) => {
      const sh = gl.createShader(t)!;
      gl.shaderSource(sh, s);
      gl.compileShader(sh);
      return sh;
    };
    const prog = gl.createProgram()!;
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, SPHERE_VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, SPHERE_FRAG));
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const lp = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(lp);
    gl.vertexAttribPointer(lp, 2, gl.FLOAT, false, 0, 0);

    const U: Record<string, WebGLUniformLocation | null> = {};
    ['u_res','u_time','u_yaw','u_lightDir','u_tapUV','u_tapTime','u_flowPhase','u_ambient','u_beat','u_loading','u_radius','u_saturation','u_contrast','u_specularI','u_rimI','u_grayscale','u_redness','u_green','u_tint','u_tintAmt','u_zoom','u_fieldPerPixel','u_base0','u_base1','u_base2','u_met0','u_met1','u_met2']
      .forEach((k) => (U[k] = gl.getUniformLocation(prog, k)));

    gl.uniform2fv(U.u_lightDir, R.lightDir);
    gl.uniform1f(U.u_ambient, R.ambient);
    gl.uniform1f(U.u_saturation, R.saturation);
    gl.uniform1f(U.u_contrast, R.contrast);
    gl.uniform1f(U.u_specularI, R.specular);
    gl.uniform1f(U.u_rimI, R.rimIntensity);
    /* fixed marble ramp (orb-studio materials): navy → recovery blue →
       sleep teal → movement terracotta; met2 = warning red overlay. */
    gl.uniform3fv(U.u_base0, RAMP.baseDeep);
    gl.uniform3fv(U.u_base1, RAMP.rec);
    gl.uniform3fv(U.u_base2, RAMP.slp);
    gl.uniform3fv(U.u_met0, RAMP.slp);
    gl.uniform3fv(U.u_met1, RAMP.mv);
    gl.uniform3fv(U.u_met2, RAMP.red);
    gl.uniform1f(U.u_zoom, 0);
    gl.uniform1f(U.u_tapTime, -1000);
    gl.uniform2f(U.u_tapUV, 0.5, 0.5);

    let sizeW = 0;
    let sizeH = 0;
    const resize = () => {
      // offsetWidth/Height = LAYOUT size — ignores ancestor CSS transforms
      // (an entrance scale must not shrink the raster → pixelation). The canvas
      // matches the element's ASPECT (non-square allowed) so a full-bleed field
      // fills the screen undistorted; the shader reads the real aspect from u_res.
      const w = canvas.offsetWidth || canvas.getBoundingClientRect().width;
      const h = canvas.offsetHeight || canvas.getBoundingClientRect().height;
      const dpr = Math.min(window.devicePixelRatio || 1, R.dprCap);
      const pw = Math.max(1, Math.round(w * dpr));
      const ph = Math.max(1, Math.round(h * dpr));
      // u_radius is a fraction of the canvas half-height. A fixed on-screen
      // diameter is that px value over the canvas height, so the ball stays a
      // constant size while the (full-bleed) canvas grows with the viewport.
      const rad = fixedDiameterPx
        ? Math.min(0.95, fixedDiameterPx / Math.max(1, h))
        : radius ?? R.sphereRadius;
      if (pw === sizeW && ph === sizeH) {
        gl.uniform1f(U.u_radius, rad);
        return;
      }
      sizeW = pw;
      sizeH = ph;
      canvas.width = pw;
      canvas.height = ph;
      gl.viewport(0, 0, pw, ph);
      gl.uniform2f(U.u_res, pw, ph);
      gl.uniform1f(U.u_radius, rad);
      gl.uniform1f(U.u_fieldPerPixel, 1.0 / Math.min(pw, ph));
    };
    resize();

    const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const cur: OrbState = { ...target.current };
    const curTint: RGB = [...target.current.tint] as RGB;
    let flowPhase = 0, yaw = 0, raf = 0, running = true;
    const start = performance.now();
    let lastTick = start;

    const hrMul = () => {
      const hr = cur.heartRate;
      if (hr < 40) return 1;
      const cl = Math.min(Math.max(hr, 40), 180);
      return Math.max(0.7, Math.min(1 + (cl / R.restingHR - 1) * 0.5, 1.6));
    };

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      if (!running) return;
      const dt = Math.min((now - lastTick) / 1000, 0.1);
      lastTick = now;
      const tgt = target.current;
      // slow ease → gradual material colour morph (not a snap)
      const k = reduce ? 1 : Math.min(1, dt * 0.9);
      cur.heartRate = lerp(cur.heartRate, tgt.heartRate, k);
      cur.redness = lerp(cur.redness, tgt.redness, k);
      cur.green = lerp(cur.green, tgt.green, k);
      cur.grayscale = lerp(cur.grayscale, tgt.grayscale, k);
      cur.loading = lerp(cur.loading, tgt.loading, k);
      cur.tintAmt = lerp(cur.tintAmt, tgt.tintAmt, k);
      for (let c = 0; c < 3; c++) curTint[c] = lerp(curTint[c], tgt.tint[c], k);

      if (!reduce) {
        flowPhase += dt * R.motionSpeed * 0.5 * hrMul();
        yaw += dt * 0.06; // gentle drift for life
      }
      const time = ((now - start) / 1000) * R.motionSpeed;
      const beat = reduce ? 1 : 1 + Math.sin(time * 1.2) * 0.03;

      gl.uniform1f(U.u_time, time);
      gl.uniform1f(U.u_yaw, yaw + (spinRef ? spinRef.current : 0));
      gl.uniform1f(
        U.u_zoom,
        zoomRef ? Math.max(0, Math.min(1, zoomRef.current)) : 0,
      );
      gl.uniform1f(U.u_flowPhase, flowPhase);
      gl.uniform1f(U.u_beat, beat);
      gl.uniform1f(U.u_loading, cur.loading);
      gl.uniform1f(U.u_grayscale, cur.grayscale);
      gl.uniform1f(U.u_redness, cur.redness);
      gl.uniform1f(U.u_green, cur.green);
      gl.uniform3fv(U.u_tint, curTint);
      gl.uniform1f(U.u_tintAmt, cur.tintAmt);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };
    raf = requestAnimationFrame(frame);

    const io = new IntersectionObserver(([e]) => (running = !!e?.isIntersecting), { threshold: 0.01 });
    io.observe(canvas);
    const onResize = () => resize();
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener('resize', onResize);
      /* no loseContext() here: React can re-run this effect on the SAME canvas
         (dev strict mode, a remount), and getContext() would then hand back the
         dead context — the orb went blank. The browser frees the context once
         the canvas is gone. */
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className ? `${styles.canvas} ${className}` : styles.canvas}
      role="img"
      aria-label={ariaLabel}
    />
  );
};

export default SphereOrb;
